import { useState, useCallback } from "react";
import { lookupBooking, requestCancellation } from "@/lib/api/bookings";
import type { Booking } from "@/types/booking";

export function useBookingLookup() {
  const [booking, setBooking] = useState<Booking | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searched, setSearched] = useState(false);

  const lookup = useCallback(async (email: string, bookingRef: string) => {
    setLoading(true);
    setError(null);
    setSearched(false);

    try {
      const result = await lookupBooking(email, bookingRef);
      setBooking(result);
      setSearched(true);
      if (!result) {
        setError(
          "No booking found with those details. Please check and try again.",
        );
      }
    } catch (err) {
      console.error("Booking lookup failed:", err);
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Fixed: now returns Promise<boolean> so BookingDetailCard can react ──────
  const cancel = useCallback(
    async (reason: string): Promise<boolean> => {
      if (!booking) return false;
      try {
        await requestCancellation(booking.id, reason);
        setBooking((prev) => (prev ? { ...prev, status: "cancelled" } : null));
        return true;
      } catch (err) {
        console.error("Cancellation failed:", err);
        return false;
      }
    },
    [booking],
  );

  const reset = useCallback(() => {
    setBooking(null);
    setError(null);
    setSearched(false);
  }, []);

  return { booking, loading, error, searched, lookup, cancel, reset };
}